import { useState, useCallback } from 'react'

import NormalTopBar from '@/components/common/bar/NormalTopBar'
import { PROVIDER_NAMES } from '@/components/auth/Tos/termsContent'
import type { Provider, TermKey, TermCheckboxes } from '@/components/auth/signup/SignupForm'
import WideButton from '@/components/common/button/WideButton'

interface TermsScreenProps {
  selectedProvider: Provider
  onClose: () => void
  onAgree: () => void
  onShowTermDetail: (termKey: TermKey) => void
}

export default function TermsOfServicePage({ selectedProvider, onClose, onAgree, onShowTermDetail }: TermsScreenProps) {
  const [checkboxes, setCheckboxes] = useState<TermCheckboxes>({
    all: false,
    required1: false,
    required2: false
  })

  const handleCheckboxChange = useCallback((type: keyof TermCheckboxes) => {
    setCheckboxes((prev: TermCheckboxes) => {
      const newCheckboxes = { ...prev }
      if (type === 'all') {
        const newValue = !prev.all
        newCheckboxes.all = newValue
        newCheckboxes.required1 = newValue
        newCheckboxes.required2 = newValue
      } else {
        newCheckboxes[type] = !prev[type]
        newCheckboxes.all = newCheckboxes.required1 && newCheckboxes.required2
      }
      return newCheckboxes
    })
  }, [])

  const isFormValid = checkboxes.required1 && checkboxes.required2

  const checkboxBaseStyle = 'w-5 h-5 rounded border-amber-300 text-amber-500 focus:ring-amber-500/50 focus:ring-2'

  return (
    <div className='fixed inset-0 z-50 flex flex-col font-sans'>
      <NormalTopBar
        title='약관 동의'
        prevButton
        onPrevButtonClick={onClose}
        textClassName='text-xl text-text-header font-semibold'
        boxClassName='bg-gradient-to-r from-amber-400 to-yellow-300'
      />
      <div className='flex-1 px-6 py-8 overflow-y-auto'>
        <div className='mb-8'>
          <h3 className='text-lg font-medium mb-2 text-gray-900'>
            {PROVIDER_NAMES[selectedProvider]} 계정으로 시작하기
          </h3>
          <p className='text-gray-500 text-sm'>서비스 이용을 위해 아래 약관에 동의해주세요.</p>
        </div>
        <div className='space-y-4'>
          <label className='flex items-center space-x-3 cursor-pointer'>
            <input
              type='checkbox'
              id='all-terms'
              checked={checkboxes.all}
              onChange={() => handleCheckboxChange('all')}
              className={checkboxBaseStyle}
            />
            <span className='text-sm text-gray-900'>모든 약관에 동의합니다</span>
          </label>
          <div className='h-px bg-amber-200 my-4'></div>
          {[
            { key: 'required2', label: '[필수] 서비스 이용약관 동의', termKey: 'required2' as TermKey },
            { key: 'required1', label: '[필수] 개인정보 취급방침 동의', termKey: 'required1' as TermKey }
          ].map(item => (
            <label key={item.key} className='flex items-center space-x-3'>
              <input
                type='checkbox'
                id={item.key}
                checked={checkboxes[item.key as keyof TermCheckboxes]}
                onChange={() => handleCheckboxChange(item.key as keyof TermCheckboxes)}
                className={checkboxBaseStyle}
              />
              <span className='text-sm text-gray-700 cursor-pointer hover:underline'>{item.label}</span>
              <span
                className='text-xs text-gray-500 cursor-pointer hover:underline'
                onClick={e => {
                  e.preventDefault()
                  onShowTermDetail(item.termKey)
                }}
              >
                자세히보기
              </span>
            </label>
          ))}
        </div>
      </div>
      <div className='px-4 pb-6'>
        <WideButton
          handleClick={onAgree}
          buttonText='동의하고 계속하기'
          className={`${!isFormValid ? 'bg-gray-300 cursor-not-allowed border-gray-300 text-gray-500' : 'text-white bg-amber-500 border-amber-500'}`}
          isDisabled={!isFormValid}
        />
      </div>
    </div>
  )
}
